import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import clsx from 'clsx';
import head from 'lodash/head';

type Props = {
  options: string[];
  onUpdate: (category: string) => void;
  defaultTab?: string;
  variant?: 'standard' | 'segmented' | 'minimal';
  size?: 'small' | 'medium';
  className?: string;
  theme?: 'color' | 'gray';
};

export default function TabFilter({
  options,
  onUpdate,
  defaultTab: _defaultTab,
  variant = 'standard',
  className,
  size = 'small',
  theme = 'color',
}: Props) {
  const optionsSet = useMemo(() => new Set(options), [options]);
  const defaultTab = useMemo(
    () => (
      optionsSet.has(_defaultTab ?? '')
        ? _defaultTab
        : head(options)
    ),
    [_defaultTab, options],
  );

  const [selected, select] = useState<string>(defaultTab ?? '');

  const handleUpdate = (tab: string) => {
    select(tab);
  };

  useEffect(() => {
    onUpdate(selected);
  }, [selected]);

  return (
    <div className={clsx('flex items-center min-w-0 gap-2', className, {
      'tab-filter-variant-segmented': variant === 'segmented',
      'tab-filter-variant-minimal': variant === 'minimal',
      'tab-filter-variant-standard': variant === 'standard',
      'tab-filter-size-small': size === 'small',
      'tab-filter-size-medium': size === 'medium',
      'tab-filter-theme-color': theme === 'color',
      'tab-filter-theme-gray': theme === 'gray',
    })}
    >
      <Tabs
        className="tab-filter-tabs"
        value={selected}
        onChange={(event, value: string) => {
          handleUpdate(value);
        }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        {options.map((option) => (
          <Tab
            key={option}
            label={option}
            value={option}
            className={clsx('tab-filter-tab', {
              'tab-filter-tab-active': selected === option,
            })}
          />
        ))}
      </Tabs>
    </div>
  );
}
